/**
 * Trade Resolution Engine
 *
 * Given two trade options (pattern-based and scanner-based),
 * validates each SL against structural levels and picks the best one.
 *
 * Decision rules (in order):
 *   1. SL validity — must be >= 0.5% from entry AND near a structural level
 *   2. If only one is valid → pick it
 *   3. If both invalid → fallback to pattern (Option A)
 *   4. Both valid → higher R:R wins
 *   5. R:R tied → tighter SL (lower risk/share) wins
 */

// ── ATR-aware tolerance ────────────────────────────────────────────────────────
// tolerance = max(1%, 0.5 × ATR%)  capped at 2%
function _tolerance(atr, refPrice) {
  if (!atr || !refPrice) return 0.02;
  const atrPct = (atr / refPrice) * 0.5;
  return Math.min(0.02, Math.max(0.01, atrPct));
}

// ── SL validity check ──────────────────────────────────────────────────────────
/**
 * Returns true if SL is:
 *   - at least 0.5% away from entry (not inside noise)
 *   - within tolerance of at least one structural level
 *
 * @param {number} entry
 * @param {number} sl
 * @param {number[]} structureLevels  - array of price levels (SMAs, swing lows, pattern levels)
 * @param {number}   atr              - 14-period ATR from market_features
 * @returns {boolean}
 */
export function isValidSL(entry, sl, structureLevels, atr) {
  if (!entry || !sl) return false;

  // Must be at least 0.5% from entry
  const distPct = Math.abs(entry - sl) / entry;
  if (distPct < 0.005) return false;

  // Must be near at least one structural level
  const tol = _tolerance(atr, sl);
  const validLevels = (structureLevels || []).filter(Boolean);

  return validLevels.some(level => {
    if (!level) return false;
    return Math.abs(sl - level) / level <= tol;
  });
}

// ── Build structure levels array ───────────────────────────────────────────────
/**
 * Assembles structural price levels from available data.
 * Used as the reference set for SL validation.
 *
 * Included:
 *   - SMA50 (always structural)
 *   - EMA20 (only if price respected it ≥ 2 times in last 15 candles)
 *   - Pattern price_level and neckline (if pattern exists)
 *   - 10-day swing low and high
 *   - Previous day low and high
 *
 * Excluded: SMA20, EMA50, Bollinger Bands
 *
 * @param {object} marketFeatures  - latest row from /api/stocks/{symbol}/technicals
 * @param {object|null} pattern    - pattern object if one exists for this stock
 * @param {array}  recentCandles   - last 10–15 OHLCV candles for EMA touch count
 * @returns {number[]}
 */
export function buildStructureLevels(marketFeatures, pattern, recentCandles) {
  const levels = [];
  const f = marketFeatures || {};

  // SMA50 — always structural
  if (f.sma_50) levels.push(f.sma_50);

  // EMA20 — conditional: only if price touched it ≥ 2 times in last 15 candles
  if (f.ema_20 && recentCandles && recentCandles.length >= 5) {
    const ema = f.ema_20;
    const touches = recentCandles.slice(-15).filter(c => {
      const low  = c.low  ?? c.low_price  ?? 0;
      const high = c.high ?? c.high_price ?? 0;
      return low <= ema * 1.005 && high >= ema * 0.995;
    }).length;
    if (touches >= 2) levels.push(ema);
  }

  // Pattern levels (geometric — most reliable)
  if (pattern) {
    if (pattern.price_level) levels.push(pattern.price_level);
    if (pattern.neckline)    levels.push(pattern.neckline);
  }

  // 10-day swing levels from recent candles
  if (recentCandles && recentCandles.length >= 2) {
    const last10 = recentCandles.slice(-10);
    const lows   = last10.map(c => c.low  ?? c.low_price  ?? 0).filter(Boolean);
    const highs  = last10.map(c => c.high ?? c.high_price ?? 0).filter(Boolean);
    if (lows.length)  levels.push(Math.min(...lows));
    if (highs.length) levels.push(Math.max(...highs));

    // Previous day levels
    const prev = recentCandles[recentCandles.length - 2];
    if (prev) {
      const pLow  = prev.low  ?? prev.low_price;
      const pHigh = prev.high ?? prev.high_price;
      if (pLow)  levels.push(pLow);
      if (pHigh) levels.push(pHigh);
    }
  }

  return levels.filter(Boolean);
}

// ── Option metrics calculator ──────────────────────────────────────────────────
/**
 * Computes all trade metrics for one option given the risk budget.
 *
 * @param {number} entry
 * @param {number} sl
 * @param {number} target
 * @param {number} capital       - user's virtual_balance
 * @param {number} riskPct       - recommended_risk_percent (e.g. 1.5)
 * @returns {object} metrics
 */
export function computeOptionMetrics(entry, sl, target, capital, riskPct) {
  const maxRisk      = capital * (riskPct / 100);
  const riskPerShare = Math.abs(entry - sl);
  // Cap position at 20% of capital so a tight SL never deploys the full portfolio
  const maxQtyByRisk = riskPerShare > 0 ? Math.max(1, Math.floor(maxRisk / riskPerShare)) : 1;
  const maxQtyCap    = entry > 0 ? Math.floor((capital * 0.20) / entry) : maxQtyByRisk;
  const qty          = Math.min(maxQtyByRisk, Math.max(1, maxQtyCap));
  const positionValue = qty * entry;
  const actualRisk   = qty * riskPerShare;
  const reward       = Math.abs(target - entry);
  const rrRatio      = riskPerShare > 0 ? parseFloat((reward / riskPerShare).toFixed(2)) : 0;
  const portfolioPct = capital > 0 ? parseFloat((positionValue / capital * 100).toFixed(2)) : 0;

  return {
    entry:         parseFloat(entry.toFixed(2)),
    sl:            parseFloat(sl.toFixed(2)),
    target:        parseFloat(target.toFixed(2)),
    qty,
    riskPerShare:  parseFloat(riskPerShare.toFixed(2)),
    positionValue: parseFloat(positionValue.toFixed(2)),
    actualRisk:    parseFloat(actualRisk.toFixed(2)),
    rrRatio,
    portfolioPct,
    maxRisk:       parseFloat(maxRisk.toFixed(2)),
  };
}

// ── Main resolver ──────────────────────────────────────────────────────────────
/**
 * Resolves the best trade option between pattern-based and scanner-based.
 *
 * @param {object} optionA  - { entry, sl, target, source: 'pattern', label: 'Pattern SL' }
 * @param {object} optionB  - { entry, sl, target, source: 'scanner', label: 'Scanner SL' }
 * @param {number[]} structureLevels
 * @param {number}   atr
 * @param {number}   capital
 * @param {number}   riskPct
 *
 * @returns {object} {
 *   recommended: 'A' | 'B',
 *   reason: string,
 *   optionA: { ...metrics, isValid, label },
 *   optionB: { ...metrics, isValid, label } | null,
 *   showComparison: boolean   ← false if only one option exists
 * }
 */
export function resolveTradeOptions(optionA, optionB, structureLevels, atr, capital, riskPct) {
  const metricsA = computeOptionMetrics(optionA.entry, optionA.sl, optionA.target, capital, riskPct);
  const validA   = isValidSL(optionA.entry, optionA.sl, structureLevels, atr);

  // If no Option B (scanner-only or pattern-only) → skip comparison
  if (!optionB) {
    return {
      recommended:    'A',
      reason:         'Only one trade setup available',
      showComparison: false,
      optionA:        { ...metricsA, isValid: validA, label: optionA.label || 'Pattern SL', source: optionA.source },
      optionB:        null,
    };
  }

  const metricsB = computeOptionMetrics(optionB.entry, optionB.sl, optionB.target, capital, riskPct);
  const validB   = isValidSL(optionB.entry, optionB.sl, structureLevels, atr);

  let recommended = 'A';
  let reason = '';

  if (validA && !validB) {
    recommended = 'A';
    reason = 'Pattern SL is at a structural level — scanner SL is not validated';
  } else if (validB && !validA) {
    recommended = 'B';
    reason = 'Scanner SL is at a structural level — pattern SL is not validated';
  } else if (!validA && !validB) {
    recommended = 'A';
    reason = 'Neither SL is at a confirmed structural level — defaulting to pattern geometry';
  } else {
    // Both valid — compare on R:R then tightness
    if (metricsB.rrRatio > metricsA.rrRatio + 0.1) {
      recommended = 'B';
      reason = `Better R:R (${metricsB.rrRatio} vs ${metricsA.rrRatio}) with valid structural SL`;
    } else if (metricsA.rrRatio > metricsB.rrRatio + 0.1) {
      recommended = 'A';
      reason = `Better R:R (${metricsA.rrRatio} vs ${metricsB.rrRatio}) with valid structural SL`;
    } else if (metricsB.riskPerShare < metricsA.riskPerShare) {
      recommended = 'B';
      reason = `Equal R:R — tighter SL (₹${metricsB.riskPerShare}/share vs ₹${metricsA.riskPerShare}/share) means better capital efficiency`;
    } else {
      recommended = 'A';
      reason = 'Pattern SL preferred — equal metrics, geometric level is more reliable';
    }
  }

  return {
    recommended,
    reason,
    showComparison: true,
    optionA: { ...metricsA, isValid: validA, label: optionA.label || 'Pattern SL', source: optionA.source },
    optionB: { ...metricsB, isValid: validB, label: optionB.label || 'Scanner SL', source: optionB.source },
  };
}
